/** The three Site templates (Role.template, surfaced on the login and /auth/me user). */
const SITE_TEMPLATES: readonly string[] = ['charger_owner', 'property_manager', 'building_admin'];

/** Presentation policy only. API scopes remain the authority for access. */
export function isSiteUser(user: { role?: string | null; template?: string | null } | null | undefined): boolean {
  if (!user || ['super_admin', 'tenant_admin', 'operator', 'viewer', 'fleet_admin'].includes(user.role ?? '')) return false;
  return SITE_TEMPLATES.includes(user.template ?? '');
}

/**
 * Does the Api treat this user as a Site caller? The client mirror of
 * isSiteCaller (apps/api/src/lib/dashboardDriverPrivacy.ts), for the pages
 * whose reads the Api strips or refuses for a Site: charger detail (Logs and
 * Audit 403, guest pass list and owner stripped) and session detail (frame
 * timeline 403, payout, refund, RFID and Stripe ids stripped).
 *
 * The server keys on the assigned Role (system_role third_party_admin, any
 * template but sales_agent), never on the JWT tier. So this keys on the
 * template, which login and /auth/me read off that same Role, and not on
 * user.role: pickSessionRole mints the HIGHER tier into the token, so a
 * native tenant_admin holding a Site grant arrives as role 'tenant_admin'
 * while the Api strips and refuses them as a Site. isSiteUser above leaves
 * that person out on purpose (the dashboard); these pages must not, or they
 * render a 403 and seed forms from stripped reads.
 *
 * A third_party_admin token with no Site template is a Site to the Api too
 * (an unclassified third party is told the least), so it counts here.
 *
 * Gap: a Role with system_role third_party_admin and NO template, held under
 * an internal JWT tier, reaches the client as { role: 'tenant_admin',
 * template: null }, indistinguishable from an operator. The Api still strips
 * and refuses it; omitUnreadEmpty below keeps its writes from clearing what
 * was stripped.
 */
export function isSiteCallerUser(
  user: { role?: string | null; template?: string | null; is_sales_agent?: boolean | null } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.template === 'sales_agent' || user.is_sales_agent === true) return false;
  return SITE_TEMPLATES.includes(user.template ?? '') || user.role === 'third_party_admin';
}

/** Charger-detail tabs the Api refuses a Site caller: GET /chargers/:id/logs and /audit answer 403. */
const SITE_REFUSED_CHARGER_TABS: ReadonlySet<string> = new Set(['logs', 'audit']);

/** The charger-detail tabs to render: every tab for staff, none that would only show a 403 for a Site. */
export function chargerTabsFor<T extends { key: string }>(tabs: readonly T[], isSite: boolean): T[] {
  return isSite ? tabs.filter((t) => !SITE_REFUSED_CHARGER_TABS.has(t.key)) : [...tabs];
}

/**
 * Drop from a write each of `keys` that the read it was seeded from did not
 * carry, while the form still holds it empty (undefined, null, '' or []).
 *
 * The Api strips fields from a Site caller's reads by leaving the key out. A
 * form seeded from that read holds [] or null for a value the caller never
 * saw, and sending it back would clear the real one. A key the read carried
 * is always kept, empty or not, so clearing a list you can see still clears
 * it; a key it did not carry goes back once it holds a value.
 */
export function omitUnreadEmpty<P extends object, K extends keyof P & string>(
  read: object,
  patch: P,
  keys: readonly K[],
): Omit<P, K> & Partial<Pick<P, K>> {
  const out = { ...patch } as Record<string, unknown>;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(read, key)) continue;
    const v = out[key];
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) delete out[key];
  }
  return out as Omit<P, K> & Partial<Pick<P, K>>;
}

export function siteDetailPath(locationId?: string | null): string {
  return locationId ? `/my-site?site=${encodeURIComponent(locationId)}` : '/my-site';
}

/** Deterministic screen-space spreading preserves each marker's true coordinates. */
export function spreadSiteMarkers(points: ReadonlyArray<{ id: string; x: number; y: number }>): Map<string, [number, number]> {
  const offsets = new Map<string, [number, number]>();
  const occupied: Array<{ x: number; y: number }> = [];
  for (const point of [...points].sort((a, b) => a.id.localeCompare(b.id))) {
    let dx = 0;
    let dy = 0;
    let attempt = 0;
    while (occupied.some((p) => Math.hypot(p.x - point.x - dx, p.y - point.y - dy) < 48)) {
      attempt += 1;
      const angle = attempt * 2.399963229728653;
      const radius = 48 * Math.sqrt(attempt);
      dx = Math.cos(angle) * radius;
      dy = Math.sin(angle) * radius;
    }
    occupied.push({ x: point.x + dx, y: point.y + dy });
    offsets.set(point.id, [dx, dy]);
  }
  return offsets;
}

export function initialSiteSelection(ids: readonly string[], isNarrow: boolean): string | null {
  return ids.length === 1 && !isNarrow ? ids[0]! : null;
}

export function selectedSiteId(ids: readonly string[], requested: string | null): string | null {
  return requested && ids.includes(requested) ? requested : ids[0] ?? null;
}
